<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template #hero>
      <PageHero page="dosen" title="Dosen" current="Dosen"
        subtitle="Direktori dosen di lingkungan Jurusan Manajemen FEB UNG." />
    </template>
    <template v-slot:default>
      <div class="min-h-screen pb-16 bg-gray-50/40">
        <!-- Search -->
        <section class="pt-10">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <div class="relative max-w-xl mx-auto">
              <div class="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400 pointer-events-none">
                <MagnifyingGlassVue class="w-5 h-5" />
              </div>
              <input v-model="search" type="text" autocomplete="off" placeholder="Cari nama, NIP, atau email..."
                class="block w-full py-3 pl-12 pr-4 text-sm text-gray-900 transition bg-white border border-gray-200 shadow-sm rounded-xl placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
          </div>
        </section>

        <!-- Grid -->
        <section class="mt-12">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <div v-if="paginatedDosen.length" class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <button v-for="(dosen, index) in paginatedDosen" :key="dosen.id || index" type="button"
                @click="viewProfile(dosen.id)"
                class="flex items-center gap-4 p-4 text-left transition-all duration-300 bg-white border border-gray-100 shadow-sm group rounded-2xl hover:shadow-lg hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                <div class="w-16 h-16 overflow-hidden bg-gray-100 rounded-xl shrink-0 ring-1 ring-gray-100">
                  <img :src="getFotoUrl(dosen.foto, dosen.nama)" :alt="dosen.nama" class="object-cover w-full h-full" />
                </div>
                <div class="min-w-0">
                  <h3
                    class="text-sm font-semibold leading-snug text-gray-900 transition-colors line-clamp-2 group-hover:text-primary">
                    {{ dosen.nama }}
                  </h3>
                  <p class="mt-0.5 text-xs text-gray-500">
                    NIP. {{ dosen.nip || "-" }}
                  </p>
                  <p class="text-xs text-gray-400 truncate">
                    {{ dosen.email || "-" }}
                  </p>
                </div>
              </button>
            </div>

            <!-- Empty state -->
            <div v-else
              class="flex flex-col items-center justify-center px-4 py-16 text-center bg-white border border-gray-100 rounded-2xl">
              <i class="mb-3 text-3xl text-gray-300 fa-solid fa-user-group"></i>
              <p class="text-sm font-medium text-gray-900">
                Dosen tidak ditemukan
              </p>
              <p class="mt-1 text-sm text-gray-500">
                Coba kata kunci pencarian lain.
              </p>
            </div>

            <!-- Pagination -->
            <nav v-if="totalPages > 1" class="flex items-center justify-center gap-1.5 mt-10">
              <button @click="goToPage(page - 1)" :disabled="page === 1"
                class="inline-flex items-center justify-center w-10 h-10 text-sm text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Sebelumnya">
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button v-for="p in totalPages" :key="p" @click="goToPage(p)" :class="[
                'inline-flex items-center justify-center w-10 h-10 text-sm font-medium rounded-lg border transition-colors',
                p === page
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary',
              ]">
                {{ p }}
              </button>
              <button @click="goToPage(page + 1)" :disabled="page === totalPages"
                class="inline-flex items-center justify-center w-10 h-10 text-sm text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Berikutnya">
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </nav>

            <Loading v-if="isLoading" :active="isLoading" :can-cancel="false" :is-full-page="false" />
            <ProfilDosen v-else v-model:isOpen="isModalOpen" :dosendata="viewDosen" />
          </div>
        </section>
      </div>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import PageHero from "@/Components/Web/PageHero.vue";
import FooterFe from "@/Components/FooterFe.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import ProfilDosen from "@/Components/Web/ProfilDosen.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

const props = defineProps({
  dosen: {
    type: Array,
    required: true,
  },
});

const search = ref("");
const page = ref(1);
const perPage = 9;
const isModalOpen = ref(false);
const viewDosen = ref(null);
const isLoading = ref(false);

const filteredDosen = computed(() => {
  const data = Array.isArray(props.dosen) ? props.dosen : props.dosen.data || [];
  if (!search.value) return data;
  const q = search.value.toLowerCase();
  return data.filter(
    (d) =>
      d.nama.toLowerCase().includes(q) ||
      (d.nip && d.nip.toLowerCase().includes(q)) ||
      (d.nidn && d.nidn.toLowerCase().includes(q)) ||
      (d.email && d.email.toLowerCase().includes(q))
  );
});

const paginatedDosen = computed(() => {
  const start = (page.value - 1) * perPage;
  return filteredDosen.value.slice(start, start + perPage);
});

const totalPages = computed(() =>
  Math.ceil(filteredDosen.value.length / perPage)
);

function goToPage(p) {
  if (p >= 1 && p <= totalPages.value) {
    page.value = p;
  }
}

// Reset to first page when the search query changes.
watch(search, () => {
  page.value = 1;
});

function getFotoUrl(foto, nama = "User") {
  if (!foto) {
    const initials = nama
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&background=e5e7eb&color=6b7280&size=128`;
  }
  if (/^https?:\/\//.test(foto) || foto.startsWith("/storage")) {
    return foto;
  }
  return "/storage/" + foto;
}

const viewProfile = async (id) => {
  isLoading.value = true;
  isModalOpen.value = true;
  viewDosen.value = null;
  try {
    const response = await fetch(`/vdosen/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }
    const data = await response.json();
    viewDosen.value = data.datadosen;
  } catch (error) {
    console.error("Error fetching data:", error.message);
  } finally {
    isLoading.value = false;
  }
};
</script>
